import '@/global.css'
import { Input } from "@/components/ui/input"
import { useState } from 'react'
import { Link } from 'react-router'
import { ROUTE } from '@/routes/constants'
import { requestCaptcha, sendResetPassword } from '@/services/generatedAPIs'
import { generateVerificationToken } from '@/lib/crypto'
import { toast } from 'sonner'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next';
import { EmailIcon } from './login-icons'

// 桌面端「申請重設密碼」表单：按 Figma 873:2 设计稿 1:1 还原。
// 右侧表单整体定位在 (1272, 0) 1920×1080 画布坐标，宽 421；元素 y 坐标按设计稿绝对值。

async function onAction(formData: FormData, t: TFunction): Promise<void> {
  const email = formData.get("email");
  if (!email || typeof email !== "string") return;
  try {
    const rsp1 = await requestCaptcha({ nonce: email });
    if (rsp1.code === 200 && !rsp1.data) {
      const rsp2 = await sendResetPassword({ t: generateVerificationToken(email) });
      if (rsp2.code === 200) toast(t('component.sendsuccess'));
      else toast(rsp2.message);
    } else {
      toast(rsp1.message);
    }
  } catch {
    toast(t('component.networkError') ?? 'Network error, please try again.');
  }
}

function RequestResetPasswordForm() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <form
      action={async (fd: FormData) => { setIsLoading(true); await onAction(fd, t); setIsLoading(false); }}
      style={{ position: 'absolute', left: 1272, top: 0, width: 421, height: 1080, pointerEvents: 'none' }}
    >
      {/* 标题「重設密碼」(canvas 1401,312)。设计稿 40 高，黑色。 */}
      <h2 style={{ position: 'absolute', left: 129, top: 312, margin: 0, width: 160, fontSize: 32, fontWeight: 600, color: '#000000', lineHeight: '40px', letterSpacing: '-0.01em', textAlign: 'center' }}>
        {t('user.reset-password-title')}
      </h2>

      {/* 描述「閣下的郵箱將會收到指示郵件，請依照指引重設密碼，郵件有效期24小時」(canvas 1272,375) */}
      <p style={{ position: 'absolute', left: 0, top: 375, margin: 0, width: 421, fontSize: 14, color: '#575C60', lineHeight: '24px' }}>
        {t('user.reset-password-subtitle')}
      </p>

      {/* 「電子郵箱」label (canvas 1272,443) */}
      <label htmlFor="email" style={{ position: 'absolute', left: 0, top: 443, fontSize: 16, fontWeight: 400, color: '#575C60', lineHeight: '20px' }}>
        {t('user.email')}
      </label>

      {/* Email 输入框 (canvas 1272,473) 421×59 #F3F3F4 r=28.5 */}
      <div style={{ position: 'absolute', left: 0, top: 473, width: 421, height: 59, pointerEvents: 'auto' }}>
        <EmailIcon aria-hidden="true" style={{ position: 'absolute', left: 24, top: 18, width: 22, height: 22, color: '#575C60', zIndex: 1 }} />
        <Input
          id="email" name="email" type="email" required
          placeholder="scholar@atelier.edu.hk"
          onInvalid={(e: any) => { e.target.setCustomValidity(t("user.email-error")); }}
          onInput={(e: any) => { e.target.setCustomValidity(""); }}
          className="pl-14 pr-5 placeholder:text-[rgba(115,119,123,0.5)]"
          style={{ width: '100%', height: 59, borderRadius: 28.5, background: '#F3F3F4', border: 0, fontSize: 16, color: '#1F1F1F' }}
        />
      </div>

      {/* 發送按钮 (canvas 1272,582) 421×73 #5BC9E8 r=36.5 */}
      <button
        type="submit"
        disabled={isLoading}
        style={{
          position: 'absolute', left: 0, top: 582, width: 421, height: 73, borderRadius: 36.5,
          background: '#5BC9E8', color: '#FFFFFF', border: 0,
          fontSize: 24, fontWeight: 500, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: isLoading ? 0.6 : 1, pointerEvents: 'auto',
        }}
        className="hover:brightness-[0.97]"
      >
        {isLoading ? '...' : t('user.send-reset-password')}
      </button>

      {/* 「登入」链接 (canvas 1465,680) */}
      <div style={{ position: 'absolute', left: 0, top: 680, width: 421, textAlign: 'center', pointerEvents: 'auto' }}>
        <Link to={ROUTE.LOGIN} style={{ fontSize: 16, color: '#5BC9E8' }} className="underline underline-offset-4">
          {t('user.sign-in')}
        </Link>
      </div>
    </form>
  );
}

export default RequestResetPasswordForm;
